import { useEffect } from 'react'

import { BonusRedemptionSheet } from '@/features/bonus/components/BonusRedemptionSheet'
import { useBonusRedemption } from '@/features/bonus/hooks/useBonusRedemption'
import { isBonusAlreadyRedeemed } from '@/features/bonus/lib/bonusRedemptionState'
import {
  buildBonusSheetClearHref,
  isBonusSheetSearchActive,
  parseBonusSheetFromQuery
} from '@/features/bonus/lib/bonusSheetSearch'
import { useAppRouter } from '@/shared/hooks/useAppRouter'
import { useTranslation } from '@/shared/hooks/useTranslation'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { alert } from '@/shared/ui/dialog'

export const BonusSheetWatcher = () => {
  const router = useAppRouter()
  const { t } = useTranslation()
  const { refetchProgress } = useUserProgress()
  const sheetSearch = parseBonusSheetFromQuery(router.query)
  const sheetOpen = isBonusSheetSearchActive(sheetSearch)
  const uid = sheetSearch.bonus ?? ''
  const partnerId = sheetSearch.partnerId ?? null

  const {
    bonus,
    isLoading,
    redeemState,
    creditedCoins,
    redeem,
    resetRedeemState
  } = useBonusRedemption(uid, { enabled: sheetOpen, partnerId })

  const alreadyRedeemed = isBonusAlreadyRedeemed(bonus)

  const closeSheet = () => {
    if (redeemState === 'success') refetchProgress()
    router.replace(buildBonusSheetClearHref(router.pathname, router.query))
  }

  useEffect(() => {
    if (!sheetOpen || isLoading) return

    if (!bonus) {
      resetRedeemState()
      router.replace(buildBonusSheetClearHref(router.pathname, router.query))
      void alert({
        variant: 'info',
        title: t('bonus.pageTitle'),
        description: t('bonus.notFound')
      })
      return
    }

    if (!bonus.redeemable && !alreadyRedeemed && redeemState !== 'success') {
      resetRedeemState()
      router.replace(buildBonusSheetClearHref(router.pathname, router.query))
      void alert({
        variant: 'warning',
        title: t('bonus.unavailableAlertTitle'),
        description: t('bonus.unavailableAlertDescription')
      })
    }
  }, [
    alreadyRedeemed,
    bonus,
    isLoading,
    redeemState,
    resetRedeemState,
    router,
    sheetOpen,
    t
  ])

  if (!sheetOpen) return null

  return (
    <BonusRedemptionSheet
      open={isLoading || Boolean(bonus)}
      bonusName={bonus?.name ?? t('bonus.pageTitle')}
      coinsAmount={bonus?.coins_amount ?? 0}
      redeemState={redeemState}
      creditedCoins={creditedCoins}
      alreadyRedeemed={alreadyRedeemed}
      onClose={closeSheet}
      onRedeem={() => void redeem()}
    />
  )
}
